"use client";

import type React from "react";
import { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  alpha,
  useTheme,
  styled,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import LeaveApplicationTable, {
  type LeaveApplicationItem,
} from "./LeaveApplicationTable";
import styles from "./EmployerLeaveView.module.css";
import { getEmployeeLeave, getEmployerLeave } from "../../api/LeaveService";
import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";

// Styled components for modern look
const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  borderRadius: "12px",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
  },
}));

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: "hidden",
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.95rem",
    minHeight: "48px",
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  "& .MuiTabs-indicator": {
    height: "3px",
    borderRadius: "3px 3px 0 0",
  },
}));

const FilterFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: "140px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, 0.8),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, 0.8),
  },
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    borderRadius: "8px",
    "&.Mui-selected": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
}));

const EmployerLeaveView: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leaveApplications, setLeaveApplications] = useState<
    LeaveApplicationItem[]
  >([]);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const fetchLeaveApplications = async () => {
    setLoading(true);
    try {
      const data = await getEmployerLeave();
      console.log({ data });
      setLeaveApplications(data);
    } catch (err) {
      console.error("Failed to fetch leave applications:", err);
      setError("Failed to load leave applications. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchLeaveApplications();
  }, []);

  useIonViewWillEnter(() => {
    fetchLeaveApplications();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, departmentFilter, statusFilter, tabValue]);

  // Calculate statistics from the API data
  const statistics = useMemo(() => {
    const pendingCount = leaveApplications.filter(
      (app) => app.status.toLowerCase() === "pending"
    ).length;
    const approvedCount = leaveApplications.filter(
      (app) => app.status.toLowerCase() === "approved"
    ).length;
    const rejectedCount = leaveApplications.filter(
      (app) => app.status.toLowerCase() === "rejected"
    ).length;
    const totalCount = leaveApplications.length;

    return {
      pendingCount,
      approvedCount,
      rejectedCount,
      totalCount,
    };
  }, [leaveApplications]);

  // Get pending applications for the Pending tab
  const pendingApplications = useMemo(() => {
    return leaveApplications.filter(
      (app) => app.status.toLowerCase() === "pending"
    );
  }, [leaveApplications]);

  // Filter applications based on search term and filters
  const filterApplications = (applications: LeaveApplicationItem[]) => {
    return applications.filter((app) => {
      // Search by name or email/ID
      const matchesSearch =
        searchTerm === "" ||
        (app.userName &&
          app.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (app.email &&
          app.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (app.employeeName &&
          app.employeeName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (app.employeeId &&
          app.employeeId.toLowerCase().includes(searchTerm.toLowerCase()));

      // Filter by department
      const matchesDepartment =
        departmentFilter === "all" ||
        (app.department &&
          app.department.toLowerCase() === departmentFilter.toLowerCase());

      // Filter by status
      const matchesStatus =
        statusFilter === "all" ||
        (app.status && app.status.toLowerCase() === statusFilter.toLowerCase());

      return matchesSearch && matchesDepartment && matchesStatus;
    });
  };

  // Get current page data for pagination
  const paginate = (items: LeaveApplicationItem[]) => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return items.slice(startIndex, startIndex + rowsPerPage);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
    // Scroll to top of table when page changes
    const tableElement = document.getElementById("leave-applications-table");
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Get filtered applications based on current tab
  const getFilteredApplications = () => {
    if (tabValue === 0) {
      return filterApplications(pendingApplications);
    }
    return filterApplications(leaveApplications);
  };

  const filteredApplications = getFilteredApplications();
  const paginatedApplications = paginate(filteredApplications);
  const totalPages = Math.ceil(filteredApplications.length / rowsPerPage);

  // Get unique departments from the data for the department filter
  const departments = useMemo(() => {
    const deptSet = new Set<string>();

    leaveApplications.forEach((app) => {
      if (app.department) {
        deptSet.add(app.department);
      }
    });

    return Array.from(deptSet);
  }, [leaveApplications]);

  return (
    <IonPage style={{ marginTop: "3%" }}>
      <IonContent>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
            Leave Management
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography
                      variant="h3"
                      fontWeight="700"
                      color="warning.main"
                    >
                      {statistics.pendingCount}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Pending Approvals
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography
                      variant="h3"
                      fontWeight="700"
                      color="success.main"
                    >
                      {statistics.approvedCount}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Approved
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography
                      variant="h3"
                      fontWeight="700"
                      color="error.main"
                    >
                      {statistics.rejectedCount}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Rejected
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography
                      variant="h3"
                      fontWeight="700"
                      color="primary.main"
                    >
                      {statistics.totalCount}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Total Applications
                  </Typography>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          <MainCard>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="600" gutterBottom>
                  Leave Applications
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and manage employee leave applications
                </Typography>
              </Box>

              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <StyledTabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="leave applications tabs"
                >
                  <Tab
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        Pending Approvals
                        {statistics.pendingCount > 0 && (
                          <Chip
                            label={statistics.pendingCount}
                            size="small"
                            color="warning"
                            sx={{ height: "20px", fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    }
                    id="tab-0"
                    aria-controls="tabpanel-0"
                  />
                  <Tab
                    label="All Applications"
                    id="tab-1"
                    aria-controls="tabpanel-1"
                  />
                </StyledTabs>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 3,
                  alignItems: { xs: "stretch", sm: "center" },
                }}
              >
                <StyledTextField
                  placeholder="Search by employee name or email"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />

                <FilterFormControl variant="outlined" size="small">
                  <InputLabel id="department-filter-label">
                    Department
                  </InputLabel>
                  <Select
                    labelId="department-filter-label"
                    id="department-filter"
                    value={departmentFilter}
                    onChange={(e) =>
                      setDepartmentFilter(e.target.value as string)
                    }
                    label="Department"
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept} value={dept.toLowerCase()}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FilterFormControl>

                <FilterFormControl variant="outlined" size="small">
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as string)}
                    label="Status"
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FilterFormControl>
              </Box>

              <Box
                role="tabpanel"
                hidden={tabValue !== 0}
                id="tabpanel-0"
                aria-labelledby="tab-0"
              >
                <LeaveApplicationTable
                  id="leave-applications-table"
                  applications={paginatedApplications}
                  showEmployee={true}
                  loading={loading}
                  error={error}
                />
                {filteredApplications.length > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                  >
                    <StyledPagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </Box>

              <Box
                role="tabpanel"
                hidden={tabValue !== 1}
                id="tabpanel-1"
                aria-labelledby="tab-1"
              >
                <LeaveApplicationTable
                  id="leave-applications-table"
                  applications={paginatedApplications}
                  showEmployee={true}
                  loading={loading}
                  error={error}
                />
                {filteredApplications.length > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                  >
                    <StyledPagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </Box>

              {filteredApplications?.length === 0 && !loading && (
                <Paper sx={{ p: 4, textAlign: "center", mt: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    No leave applications found matching your criteria.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </MainCard>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default EmployerLeaveView;
